import { addMinutes, format } from 'date-fns';
import React from 'react';
import { useForm } from 'react-hook-form';

import EditorForm from '@components/form/EditorForm/EditorForm';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useAiCommentDeleteMutation } from '@pages/ActionItem/services/useAiCommentDeleteMutation';
import { useAiCommentUpdateMutation } from '@pages/ActionItem/services/useAiCommentUpdateMutation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import AttachmentsField from '../CompletionReportModal/components/AttachmentsField/AttachmentsField';
import ModalHeader from '../ModalHeader/ModalHeader';
import {
  COMMENT_EDIT_FIELDS,
  getCommentEditDefaultValue,
  schemaEditComment,
} from './constants/schemaEditComment';
import style from './style.module.scss';

const EditCommentModal = ({ comment, aiType }) => {
  const { t } = useTranslation();
  const { setModalClose, setSecondaryModal } = useModalActions();
  const userTimezone = -new Date().getTimezoneOffset();

  const { data: profileData } = useUserProfileQuery();
  const dateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const { mutate: commentUpdateMutate, isLoading: isUpdateLoading } = useAiCommentUpdateMutation();
  const { mutate: commentDeleteMutate, isLoading: isDeleteLoading } = useAiCommentDeleteMutation();

  const isDisabledButtons = isUpdateLoading || isDeleteLoading;

  const userImage =
    comment?.user_pic && !comment?.user_pic?.includes('placeholder')
      ? comment.user_pic
      : profileData.customer.comment_image;

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: schemaEditComment,
    defaultValues: getCommentEditDefaultValue(comment),
  });

  const handleDeleteComment = () => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Delete comment'),
        bodyContent: t('Are you sure you want to delete this comment? This action cannot be undone.'),
        confirmText: t('Yes'),
        onConfirm: () => {
          commentDeleteMutate(
            { aiType, actionItemId: comment.action_list_id, commentId: comment.id },
            {
              onSuccess: () => {
                showToast({ variant: 'success', message: t('Comment was added successfully.') });

                setModalClose();
              },
            },
          );
        },
      },
    });
  };

  const handleCancelComment = () => {
    if (isDirty) {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: t('Are you sure you want to exit without saving the changes?'),
          onConfirm: () => setModalClose(),
        },
      });
    } else {
      setModalClose();
    }
  };

  const handleSaveComment = handleSubmit((formData) => {
    commentUpdateMutate(
      { ...formData, aiType, actionItemId: comment.action_list_id, commentId: comment.id },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Comment has been successfully changed.') });

          setModalClose();
        },
      },
    );
  });

  return (
    <div className={style.editCommentModal}>
      <ModalHeader title={t('Edit comment')} onClose={setModalClose} />
      <div className={style.modalBody}>
        <div className={style.commentInfo}>
          <img className={style.userImage} src={userImage} alt={comment.user_name} />
          <div>
            <div className={style.userName}>{comment.user_name}</div>
            <div className={style.commentDate}>
              {format(addMinutes(comment.time_created, userTimezone), dateFormat)}
            </div>
          </div>
        </div>
        <EditorForm name={COMMENT_EDIT_FIELDS.COMMENT} control={control} height={290} />
        <AttachmentsField name={COMMENT_EDIT_FIELDS.ATTACHMENTS} control={control} label="Attachments" />
      </div>
      <div className={style.modalFooter}>
        <Button
         type="secondary" 
         className={style.modalDeleteBtn}
          text={t('Delete')}
          disabled={isDisabledButtons}
          onClick={handleDeleteComment}
        />
        <div className={style.modalFooterBtns}>
          <Button
            type="secondary"
            text={t('Cancel')}
            disabled={isDisabledButtons}
            onClick={handleCancelComment}
          />
          <Button
            type="primary"
            text={t('Save')}
            variant="medium"
            disabled={isDisabledButtons}
            onClick={handleSaveComment}
          />
        </div>
      </div>
    </div>
  );
};

export default EditCommentModal;
